import { ReactNode } from "react";

import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Adg4Button,
    ButtonColor,
    ButtonVariant
} from "@/components/adg4/button/src";
import { wacToken } from "@/libraries/design/foundations";
import {
    Row,
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

const ctaStyles = css({
    "maxWidth": "1440px",
    "margin": `${token("space.200")} auto`,

    "textAlign": "center",

    "&.is-visible": {}
});

const h3Styles = css({
    fontSize: "40px !important",
    lineHeight: "48px !important",
    [media.below.sm]: {
        fontSize: "32px !important",
        fontWeight: "700 !important",
        lineHeight: "40px !important"
    }
});

const typographyOverrides = css({
    color: `${wacToken("color.neutral.light0")} !important`,
    marginBlockEnd: `${token("space.200")} !important`
});

const polygonStyles = css({
    background: "var(--background-color)",
    clipPath:
        "polygon(1.5% 0, 70% 3%, 70% 13%, 97% 9%, 97.5% 89%, 40% 100%, 39.9% 90%, 1% 88%)",
    [media.below.sm]: {
        clipPath: "polygon(0 0, 100% 4%, 100% 97%, 28% 100%, 28% 87%, 0 87%)"
    },
    padding: `${token("space.1000")} 0`
});

export type CtaWithPolygonBackgroundProps = {
    header: ReactNode | string;
    paragraph?: ReactNode | string;
    ctaText: ReactNode | string;
    ctaUrl: string;
    ctaLabel: string;
    productKey: ProductKeyTypes;
    backgroundColor?: "#1868DB" | "#101214";
    buttonVariant?: ButtonVariant;
    buttonColor?: ButtonColor;
};

export const ADG4CtaWithPolygonBackground = ({
    header,
    paragraph,
    ctaText,
    ctaUrl,
    ctaLabel,
    productKey,
    backgroundColor = "#101214",
    buttonVariant = "secondary",
    buttonColor = "white"
}: CtaWithPolygonBackgroundProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    const noParagraphSpacing = !paragraph ? token("space.400") : 0;

    return (
        <TypographyStyles>
            <section
                css={[ctaStyles, typographyOverrides]}
                ref={scrollRef}
                className={sectionIsVisible ? "is-visible" : ""}
            >
                <div
                    style={
                        {
                            "--background-color": backgroundColor
                        } as React.CSSProperties
                    }
                    className="polygon"
                    css={polygonStyles}
                >
                    <Row layout="8">
                        <div>
                            <h2
                                className="header h3"
                                css={[h3Styles, typographyOverrides]}
                                style={{ marginBottom: noParagraphSpacing }}
                            >
                                {header}
                            </h2>
                            {paragraph && (
                                <Row layout="8">
                                    <p
                                        className=".body-lg"
                                        css={typographyOverrides}
                                    >
                                        {paragraph}
                                    </p>
                                </Row>
                            )}
                            <Adg4Button
                                css={css({ marginBlockEnd: 32 })}
                                variant={buttonVariant}
                                color={buttonColor}
                                colorAgainst={
                                    backgroundColor == "#101214"
                                        ? "black"
                                        : "other"
                                }
                                href={ctaUrl}
                                dataEventProps={{
                                    "data-product-key": productKey,
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "ctaSection",
                                    "data-label-english": ctaLabel
                                }}
                            >
                                {ctaText}
                            </Adg4Button>
                        </div>
                    </Row>
                </div>
            </section>
        </TypographyStyles>
    );
};
